import { Card, Loader } from "~/ui-rtk/components/ui/common"
import { Image } from "~/ui-rtk/components/ui/common/Image"
import { H3 } from "~/ui-rtk/components/ui/typography"
import {
  COEF_METRICS,
  CREATIVE_REPORTS_METRICS_MAP,
  MONETARY_METRICS,
  PERCENTAGE_METRICS,
  TMetricsMapKey,
} from "../connect"
import {
  toCoef,
  toCurrency,
  toNumber,
  toPercentage,
} from "~/ui-rtk/utils/number-utils"
import { Link } from "react-router-dom"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { SOURCES_MAP } from "~/ui-rtk/constants/sources"
import { useState } from "react"
import { ChevronDownIcon, RocketLaunchIcon } from "@heroicons/react/24/outline"
import useAdForgeAccess from "~/ui-rtk/hooks/ad-forge-access"
import useDemoMode from "~/ui-rtk/hooks/demo-mode"
import { parseArrayString } from "../utils"

export default function CreativeReportCard({
  card,
  primaryIdKey,
  skipTitle,
  metricsNames,
  imgUrl,
  assetMediaType,
  isMediaLoading = false,
  onOpenDrawer,
  onFilterByName,
  disabledPillTypes = [],
  filterPillTypes = [],
  onBlastClick,
  onCardClick,
  isAggregated = false,
}: {
  card: Record<TMetricsMapKey | string, string>
  skipTitle?: boolean
  primaryIdKey: string
  metricsNames: TMetricsMapKey[]
  imgUrl?: string
  /** AMS-resolved media type; AMS URLs are extensionless so the Image
   *  component can't sniff video on its own. */
  assetMediaType?: "IMAGE" | "VIDEO"
  /** The creative's media is still being read (from Meta): show a loader over the preview. */
  isMediaLoading?: boolean
  isAggregated?: boolean
  onOpenDrawer?: (params: {
    name: string
    type: "campaign" | "adset" | "ad"
    card: Record<string, string>
  }) => void
  onFilterByName?: (params: {
    name: string
    type: "campaign" | "adset" | "ad"
  }) => void
  disabledPillTypes?: Array<"campaign" | "adset" | "ad">
  filterPillTypes?: Array<"campaign" | "adset" | "ad">
  onBlastClick?: (creativeData: {
    creativeId?: string
    objectStoryId?: string
    adId?: string
    imageUrl?: string
    destinationUrl?: string
    card: Record<string, string>
  }) => void
  onCardClick?: (card: Record<string, string>) => void
}) {
  const { hasAccess: hasBlasterAccess } = useAdForgeAccess()
  const { isEnabled: isDemoEnabled } = useDemoMode()

  // Extract creative identifiers for blasting. In aggregated mode the card has
  // no per-channel dimensions, so we blast the top-spend Meta creative pulled
  // via the top_meta_* measures instead.
  const handleBlastClick = () => {
    if (!onBlastClick) return

    onBlastClick(
      isAggregated
        ? {
            creativeId: card.top_meta_creative_id,
            objectStoryId: card.top_meta_object_story_id,
            adId: card.top_meta_ad_id,
            imageUrl: card.top_spend_image_url,
            destinationUrl: card.top_meta_destination_url,
            card,
          }
        : {
            creativeId: card.creative_id,
            objectStoryId: card.object_story_id,
            adId: card.ad_id,
            imageUrl: card.image_url,
            destinationUrl: card.destination_url,
            card,
          },
    )
  }
  const [expandedSections, setExpandedSections] = useState<{
    adsets: boolean
    campaigns: boolean
  }>({
    adsets: false,
    campaigns: false,
  })

  const toggleSection = (section: "adsets" | "campaigns") => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  // Parses array strings (JSON array, ClickHouse array_agg format, or
  // comma-separated) into pill items
  const renderPills = parseArrayString

  // Unique, lowercased media channels for this card. In aggregated mode this is
  // the full list of channels the ad name ran in (for multi-icon rendering); in
  // grouped mode it's the single per-row channel.
  const rawChannels = isAggregated
    ? (renderPills(card.all_media_channels) ?? [])
    : [card.media_channel].filter((channel): channel is string => !!channel)

  const mediaChannels = rawChannels
    .map(channel => channel.toLowerCase())
    .filter((channel, idx, arr) => arr.indexOf(channel) === idx)

  // Channels we have an icon for, in a stable order.
  const iconChannels = mediaChannels.filter(
    channel => !!SOURCES_MAP[channel.toUpperCase() as keyof typeof SOURCES_MAP],
  )

  // Blast targets Meta. Eligible when Meta is present among this card's
  // channels and (in aggregated mode) we resolved a top-spend Meta creative id.
  const isMetaCreative =
    mediaChannels.includes("meta") &&
    (!isAggregated || !!card.top_meta_creative_id)

  // Card-level click opens the details drawer. Clicks that originate from
  // interactive children (View Ad link, pills, Blast button, see-more toggles)
  // are ignored so their own handlers keep working.
  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!onCardClick) return
    const target = event.target as HTMLElement
    if (target.closest("a,button")) return
    onCardClick(card)
  }

  const handleNameClick = (name: string, type: "campaign" | "adset" | "ad") => {
    // Filter pill types trigger filtering instead of drawer
    if (filterPillTypes.includes(type) && onFilterByName) {
      onFilterByName({ name, type })
      return
    }

    if (!onOpenDrawer) return

    // Don't trigger drawer for disabled types
    if (disabledPillTypes.includes(type)) {
      return
    }

    // Pass the name, type, and card data to the parent
    // The parent will query the cube to get the ID and then open the drawer
    onOpenDrawer({ name, type, card })
  }

  const renderNamesPillsSection = (
    title: string,
    namesStr: string | undefined,
    isExpanded: boolean,
    onToggleExpand: () => void,
    type: "campaign" | "adset" | "ad",
  ) => {
    const names = renderPills(namesStr)
    if (!names || names.length === 0) return null

    const itemsToShow = isExpanded ? names : names.slice(0, 3)
    const hasMore = names.length > 3
    const isFilterable = filterPillTypes.includes(type)
    const isDisabled = !isFilterable && disabledPillTypes.includes(type)

    return (
      <div className="py-2 px-3 border-t border-basic-blue/40">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-gray-200 font-medium">{title}:</span>
          {hasMore && (
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 text-xs text-brand hover:text-brand/80 font-medium transition-colors duration-200"
            >
              {isExpanded ? "See Less" : `See More (${names.length})`}
              <ChevronDownIcon
                className={cn(
                  "w-3 h-3 transition-transform duration-300",
                  isExpanded && "rotate-180",
                )}
              />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {itemsToShow.map((name: string, idx: number) => (
            <button
              key={`${name}-${idx}`}
              onClick={() => handleNameClick(name, type)}
              disabled={isDisabled}
              tabIndex={isDisabled ? -1 : 0}
              className={cn(
                "inline-block px-3 py-1 rounded-full text-xs text-white font-medium transition-all duration-150 whitespace-nowrap",
                isDisabled
                  ? "bg-system-blue-700/50 cursor-not-allowed opacity-60"
                  : "bg-system-blue-600 hover:bg-brand cursor-pointer hover:scale-105 hover:shadow-lg",
                isDemoEnabled && "blur-md",
              )}
              title={
                isDisabled
                  ? `${type.charAt(0).toUpperCase() + type.slice(1)} details not available in Creative Reports`
                  : isFilterable
                    ? `Filter by ${type}: ${name}`
                    : `Click to view ${type} details: ${name}`
              }
            >
              {name.length > 15 ? `${name.substring(0, 15)}...` : name}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <Card
      className={cn(
        "creative-report-card p-0 group rounded-system-xl relative bg-system-blue-800 border border-basic-blue/40 hover:border-brand/60 transition-all duration-300 hover:shadow-lg hover:shadow-brand/20 hover:-translate-y-1 hover:z-[9999] flex flex-col h-full overflow-hidden",
        onCardClick && "cursor-pointer",
      )}
      onClick={handleCardClick}
    >
      {/* Background Pattern */}

      {/* Image Section */}
      <div className="relative p-system-sm flex-shrink-0">
        <div className="relative aspect-square w-full rounded-system-lg overflow-hidden bg-basic-black/50 border border-basic-blue/20">
          <Image
            src={imgUrl ?? card.image_url}
            // thumbnail_url (platform CDN, ~30d valid) rescues rows whose
            // md_image_url is a guessed S3 path that 404s for newer assets.
            fallback={card.image_url ?? card.thumbnail_url}
            alt={card[primaryIdKey]}
            allowVideoPlayback={false}
            mediaType={assetMediaType === "VIDEO" ? "video" : undefined}
            classes={{
              wrapper: cn("w-full h-full", isDemoEnabled && "blur-md"),
              image:
                "w-full h-full object-cover transition-transform duration-300 group-hover:scale-105",
            }}
          />
          {isMediaLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-basic-black/50">
              <Loader size={32} />
            </div>
          )}
          {/* Overlay gradient */}
        </div>
      </div>

      {/* Media Channel and View Ad - Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {iconChannels.length > 0 && (
          <div className="absolute left-system-lg top-system-lg flex items-center gap-system-xs pointer-events-auto">
            {iconChannels.map(channel => {
              const source =
                SOURCES_MAP[channel.toUpperCase() as keyof typeof SOURCES_MAP]
              return (
                <img
                  key={channel}
                  src={source.icon}
                  alt={source.name}
                  title={source.name}
                  className="h-8 w-8 object-contain"
                />
              )
            })}
          </div>
        )}
        {card.creative_url && (
          <Link
            to={card.creative_url}
            target="_blank"
            className="inline-flex absolute right-system-lg top-system-lg items-center px-3 py-1.5 bg-gradient-to-r from-brand to-brand/80 hover:from-brand/90 hover:to-brand text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-brand/30 transform hover:scale-105 pointer-events-auto"
          >
            <svg
              className="w-4 h-4 mr-1.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            View Ad
          </Link>
        )}
      </div>

      {/* Content Section */}
      <div className="relative p-system-sm bg-basic-dark-blue/80 flex flex-col flex-grow min-h-0">
        {!skipTitle && (
          <div className="mb-system-xs">
            <H3
              className={cn(
                "text-white font-bold text-lg leading-tight truncate group-hover:text-brand transition-colors duration-200",
                isDemoEnabled && "blur-md",
              )}
            >
              {card[primaryIdKey]}
            </H3>
          </div>
        )}

        {/* Metrics Section - Fixed Height */}
        <div className="bg-basic-black/80 backdrop-blur-sm rounded-b-system-lg border border-basic-blue/40 min-h-64 overflow-y-auto flex-1">
          <div className="divide-y divide-basic-blue/30">
            {metricsNames.map((metric, idx) => {
              // Skip array and percentage metrics from the rows
              if (
                [
                  "all_adset_names",
                  "all_campaign_names",
                  "all_ad_names",
                  "serving_adsets_percent_of_total",
                  "serving_campaigns_percent_of_total",
                  "serving_ads_percent_of_total",
                ].includes(metric)
              ) {
                return null
              }
              let val = toNumber(card[metric])
              if (MONETARY_METRICS.includes(metric)) {
                val = toCurrency(card[metric])
              }
              if (COEF_METRICS.includes(metric)) {
                val = toCoef(card[metric])
              }
              if (PERCENTAGE_METRICS.includes(metric)) {
                val = toPercentage(card[metric])
              }

              return (
                <div
                  key={metric}
                  className={cn(
                    "flex justify-between items-center py-system-xs px-system-sm transition-all duration-200 hover:bg-basic-blue/20",
                    {
                      "border-t border-basic-blue/40": idx > 0,
                    },
                  )}
                >
                  <span className="text-sm text-gray-200 font-medium">
                    {CREATIVE_REPORTS_METRICS_MAP[metric]}
                  </span>
                  <span className="text-sm font-bold text-white bg-basic-dark-blue/80 px-2 py-1 rounded">
                    {val}
                  </span>
                </div>
              )
            })}

            {/* Serving Ad Set Names as Pills - Clickable */}
            {renderNamesPillsSection(
              "Serving Ad Sets",
              card.all_adset_names,
              expandedSections.adsets,
              () => toggleSection("adsets"),
              "adset",
            )}

            {/* Serving Campaign Names as Pills - Clickable */}
            {renderNamesPillsSection(
              "Serving Campaigns",
              card.all_campaign_names,
              expandedSections.campaigns,
              () => toggleSection("campaigns"),
              "campaign",
            )}
          </div>
        </div>

        {/* Blast Button - Hover to reveal at bottom */}
        {hasBlasterAccess && isMetaCreative && onBlastClick && (
          <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
            <button
              onClick={handleBlastClick}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/30 transform hover:scale-105"
              title="Blast this creative to multiple ad sets"
            >
              <RocketLaunchIcon className="w-4 h-4" />
              Ad Forge
            </button>
          </div>
        )}
      </div>
    </Card>
  )
}
